import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { beFindReaderV1 } from 'actions/pos/beFindReader.action';
import determinePosReaderId from 'bl/pos/determinePosReaderId.bl';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import POS_READER_STATUS from 'constants/pos/posReaderStatus.const';
import useAef from 'hooks/useAef.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import logger from 'utils/logger.util';

import CN from './BannerReaderStatus.module.scss';

const CTAG = 'BannerReaderStatus';
const L = logger(CTAG);

const ReaderBanner = ({ status }) => {
  const { t } = useTranslation();
  L.debug('BannerReaderStatus()', 'status:', status);

  if (status === POS_READER_STATUS.online || status === POS_READER_STATUS.noReader || status === POS_READER_STATUS.ordered) {
    return (
      <div
        data-bem="BannerReaderStatuses--online"
        className={cn({
          [CN.container]: true,
          [CN.online]: true,
        })}
      >
        <div className={CN.text}>
          <span className={CN.statusTitle}>{t('slickPay.readerCard.title')}</span>
          <div className={CN.badgeContainer}>
            <Badge variant="success" invertedDark>
              <div className={CN.buttonInner}>
                <Icon variant="checkCircleSolid" width="md" />
                <span className={CN.badgeText}>{t('slickPay.readerCard.statuses.online')}</span>
              </div>
            </Badge>
          </div>

          <span className={CN.description}>{t('slickPay.readerCard.descriptions.online')}</span>
        </div>
      </div>
    );
  }
  if (status === POS_READER_STATUS.offline) {
    return (
      <div
        data-bem="BannerReaderStatuses--offline"
        className={cn({
          [CN.container]: true,
          [CN.offline]: true,
        })}
      >
        <div className={CN.text}>
          <span className={CN.statusTitle}>{t('slickPay.readerCard.title')}</span>
          <div className={CN.badgeContainer}>
            <Badge variant="alert">
              <div className={CN.buttonInner}>
                <Icon variant="off" width="md" />
                <span className={CN.badgeText}>{t('slickPay.readerCard.statuses.offline')}</span>
              </div>
            </Badge>
          </div>
          <span className={CN.description}>{t('slickPay.readerCard.descriptions.offline')}</span>
        </div>
      </div>
    );
  }
  if (status === POS_READER_STATUS.inactive) {
    return (
      <div
        data-bem="BannerReaderStatuses--inactive"
        className={cn({
          [CN.container]: true,
          [CN.inactive]: true,
        })}
      >
        <div className={CN.text}>
          <span className={CN.statusTitle}>{t('slickPay.readerCard.title')}</span>
          <div className={CN.badgeContainer}>
            <Badge variant="alert">
              <div className={CN.buttonInner}>
                <Icon variant="off" width="md" />
                <span className={CN.badgeText}>{t('slickPay.readerCard.statuses.inactive')}</span>
              </div>
            </Badge>
          </div>
          <span className={CN.description}>{t('slickPay.readerCard.descriptions.inactive')}</span>
        </div>
      </div>
    );
  }
  return <div />;
};

const BannerReaderStatus = () => {
  const dispatch = useDispatch();
  const salonId = useSelector(selectCurrentSalonId);
  const beFindReader = useCallback((...args) => dispatch(beFindReaderV1(...args)), [dispatch]);

  const { value: status, isLoaded } = useAef(async () => {
    const { readerStatus } = await determinePosReaderId({ salonId, beFindReader });
    return readerStatus;
  }, [salonId, beFindReader]);

  useEffect(() => void window.scrollTo(0, 0), []);

  return isLoaded && <ReaderBanner status={status} />;
};

export default BannerReaderStatus;
